import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { NfPlaybookActivity } from "../../../graphql.js";
import {
  type IPlaybookConfigState,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import NfActivitiesConfigurator from "./NfActivitiesConfigurator.js";

type PlaybookConfigNfActivitiesConfigScreenProps = IPlaybookWizardStepProvidedProps;

const PlaybookConfigNfActivitiesConfigScreen: FC<
  PlaybookConfigNfActivitiesConfigScreenProps & Partial<IPlaybookWizardStepInjectedProps>
> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const configState = useSelector<IAppState, Partial<IPlaybookConfigState>>(
    (state) => state.playbookAdminState.playbookConfigState,
  );

  const [isSavingPlaybook, setSavingPlaybook] = useState<boolean>(false);

  useEffect(() => {
    const nfWorkflow = configState.nfWorkflow;
    const nfPlaybookActivities: NfPlaybookActivity[] | undefined = nfWorkflow?.activities
      ?.map((activity) => {
        if (activity == undefined) return null;

        const configuredActivity = configState.nfActivities?.find(
          (configuredActivity) => configuredActivity.name === activity.name,
        );

        return {
          ...activity,
          ...configuredActivity,
        };
      })
      .filter((a): a is NfPlaybookActivity => a != undefined);
    dispatch(updatePlaybookConfigState({ nfActivities: nfPlaybookActivities }));
  }, []);

  const updateNfActivities = (nfActivities: NfPlaybookActivity[]) => {
    props.updateWizardMessage?.(null); // reset any validation error
    dispatch(updatePlaybookConfigState({ nfActivities }));
  };

  const isLastScreen: boolean =
    configState.playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS ||
    configState.playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES;

  const handleSubmitData = () => {
    // validation
    if (isLastScreen) {
      props.updateWizardMessage?.(null);
      setSavingPlaybook(true);
      return dispatch(savePlaybook())
        .then(() => {
          setSavingPlaybook(false);
          return props.proceedWithNextStep?.();
        })
        .catch((error: Error) => {
          props.updateWizardMessage?.(error.message);
          setSavingPlaybook(false);
        });
    }

    return props.proceedWithNextStep?.();
  };

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
        description: configState.playbookType ? getPageDescription(configState.playbookType) : undefined,
      }}
      wizardFooterData={{
        nextButtonProps: {
          label: isLastScreen ? "Save Playbook" : "Next",
          isLoading: isLastScreen ? isSavingPlaybook : false,
          onPress: handleSubmitData,
        },
      }}
    >
      <NfActivitiesConfigurator
        nfActivitiesConfig={configState.nfActivities || []}
        setNfActivitiesConfig={updateNfActivities}
      />
    </PlaybookWizardStep>
  );
};

const getPageDescription = (playbookType: PLAYBOOK_TYPE) => {
  switch (playbookType) {
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS:
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES: {
      return "Select the computing environment where each workflow activity will be executed.";
    }
  }

  return;
};

export default PlaybookConfigNfActivitiesConfigScreen;
